// components/ComplaintLineEditor.js - Guided entry for one complaint line.
//
// Briefing section 6, point by point: tiles instead of dropdowns, one active question
// at a time, answered steps collapse to a line with a Change button, steps that do not
// apply are skipped with a note, progress per phase, and a save button that says what
// the save will do. Saving is never blocked: the status model absorbs incompleteness.
import React, { useMemo, useState } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import api, { getErrorMessage } from '../utils/api';
import {
  FIELD_LABEL_KEYS, PHASE2_FIELDS, applyChoice, changedFields, elementTypesFor,
  nextOpenStep, optionsFor, phaseProgress, requiredPhase1, saveIntent, valueLabel,
} from '../utils/complaints';

const PHASE1_ORDER = ['article_type', 'element_type', 'main_complaint', 'detail'];

// `resolveComplaintId` is for callers that have no dossier yet (the order screen): it creates
// it on the first save, so cancelling the editor leaves nothing behind.
export default function ComplaintLineEditor({ complaintId, resolveComplaintId, line, taxonomy, onSaved, onClose }) {
  const { t } = useLang();
  const toast = useToast();
  const isNew = !line?.id;
  const [draft, setDraft] = useState(() => ({ ...(line || {}) }));
  const [editing, setEditing] = useState(null);   // field reopened via Change
  const [saving, setSaving] = useState(false);

  const intent = useMemo(() => saveIntent(draft, taxonomy), [draft, taxonomy]);
  const p1 = phaseProgress(draft, taxonomy, 1);
  const p2 = phaseProgress(draft, taxonomy, 2);
  const open = editing || nextOpenStep(draft, taxonomy);
  const diff = changedFields(line || {}, draft);
  // A reopened line with unchanged answers still has something to save: the close.
  const dirty = Object.keys(diff).length > 0 || (!isNew && line.status !== intent.status);

  const choose = (field, value) => {
    setDraft((d) => applyChoice(d, field, value, taxonomy));
    setEditing(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const dossierId = complaintId || await resolveComplaintId();
      const { data } = isNew
        ? await api.post(`/api/complaints/${dossierId}/lines`, diff)
        : await api.patch(`/api/complaints/${dossierId}/lines/${line.id}`, diff);
      toast.success(t(`complaints.saved.${data?.status || intent.status}`, t('complaints.saved')));
      onSaved(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally { setSaving(false); }
  };

  const label = (f) => t(FIELD_LABEL_KEYS[f]);
  const elementApplies = elementTypesFor(taxonomy, draft.article_type).length > 0;

  // One step: collapsed with Change once answered, tiles while open, hidden while upcoming.
  const renderStep = (field) => {
    if (field === 'element_type' && draft.article_type && !elementApplies) {
      return (
        <div key={field} className="step-done">
          <span className="step-done-check">–</span>
          <span className="step-done-label">{label(field)}</span>
          <span className="step-done-value step-skipped">
            {t('complaints.editor.noelement').replace('{article}', draft.article_type)}
          </span>
        </div>
      );
    }
    const value = draft[field];
    if (open === field) {
      const care = field === 'causer' || field === 'cost_bearer';
      return (
        <div key={field} className="step-active">
          <div className="step-question">{label(field)}</div>
          {care && <div className="care-hint">{t('complaints.editor.carehint')}</div>}
          {field === 'cost_bearer' && draft._costBearerSuggested && (
            <div className="form-hint" style={{ marginBottom: 8 }}>{t('complaints.editor.suggested')}</div>
          )}
          <div className="tile-grid" role="group" aria-label={label(field)}>
            {optionsFor(taxonomy, field, draft).map((o) => (
              <button key={o.value} type="button"
                className={`tile ${value === o.value ? 'selected' : ''}`}
                aria-pressed={value === o.value}
                onClick={() => choose(field, o.value)}>
                {o.label}
              </button>
            ))}
          </div>
          {editing === field && value && (
            <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => setEditing(null)}>
              {t('complaints.editor.keep')}
            </button>
          )}
        </div>
      );
    }
    if (value) {
      return (
        <div key={field} className="step-done">
          <span className="step-done-check">✓</span>
          <span className="step-done-label">{label(field)}</span>
          <span className="step-done-value">
            {valueLabel(taxonomy, field, value)}
            {field === 'cost_bearer' && draft._costBearerSuggested && (
              <span className="step-skipped"> · {t('complaints.editor.suggested.short')}</span>
            )}
          </span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(field)}>
            {t('complaints.editor.change')}
          </button>
        </div>
      );
    }
    return null; // upcoming: listed once below the open step
  };

  const upcoming = (fields) => fields.filter((f) => !draft[f] && f !== open);
  const upcoming1 = upcoming(requiredPhase1(draft, taxonomy));
  const upcoming2 = upcoming(PHASE2_FIELDS);

  const saveLabel = intent.status === 'solved' ? t('complaints.editor.save.solved')
    : intent.complete ? t('complaints.editor.save.complete')
    : t('complaints.editor.save.incomplete');

  return (
    <div>
      <div className="phase-head">
        <div className="section-title" style={{ margin: 0 }}>{t('complaints.phase1')}</div>
        <span className={`phase-progress ${p1.done === p1.total ? 'complete' : ''}`}>{p1.done}/{p1.total}</span>
      </div>
      {PHASE1_ORDER.map(renderStep)}
      {upcoming1.length > 0 && (
        <div className="step-upcoming">{t('complaints.editor.next')}: {upcoming1.map(label).join(' · ')}</div>
      )}
      <div className="form-group" style={{ marginTop: 14 }}>
        <label className="form-label" htmlFor={`desc-${line?.id || 'new'}`}>{t('complaints.field.description')} <span className="step-skipped">({t('complaints.optional')})</span></label>
        <textarea id={`desc-${line?.id || 'new'}`} className="input-field" rows={2} style={{ minHeight: 64 }}
          value={draft.description || ''} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
      </div>

      <div className="phase-head">
        <div className="section-title" style={{ margin: 0 }}>{t('complaints.phase2')}</div>
        <span className={`phase-progress ${p2.done === p2.total ? 'complete' : ''}`}>{p2.done}/{p2.total}</span>
      </div>
      {p1.done < p1.total && p2.done === 0 && open !== 'solution' && !PHASE2_FIELDS.includes(editing) ? (
        <div className="step-upcoming">{t('complaints.editor.verdictlater')}</div>
      ) : (
        <>
          {PHASE2_FIELDS.map(renderStep)}
          {upcoming2.length > 0 && !PHASE2_FIELDS.includes(open) && (
            <div className="step-upcoming">{t('complaints.editor.next')}: {upcoming2.map(label).join(' · ')}</div>
          )}
        </>
      )}

      <div className="save-bar">
        <button type="button" className={`btn ${intent.complete ? 'btn-primary' : 'btn-secondary'}`}
          disabled={saving || (!isNew && !dirty)} onClick={save}>
          {saving ? t('complaints.saving') : saveLabel}
        </button>
        <div className="save-missing">
          {!isNew && !dirty ? t('complaints.editor.nochanges')
            : intent.complete
              ? <>{t('complaints.editor.becomes')} <strong>{t(`complaints.status.${intent.status}`)}</strong></>
              : <>{t('complaints.editor.missing')}: <strong>{intent.missing.map(label).join(', ')}</strong> · {t(`complaints.status.${intent.status}`)}</>}
        </div>
        {onClose && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{t('common.cancel')}</button>
        )}
      </div>
    </div>
  );
}
